import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";

import { CloseIcon } from "../../../components/Icons";
import { GvPrimaryButton } from "../../../components";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { useModal } from "../../../context/ModalContext";
import { userActivityLogger } from "../../../utils/mixpanel";

const scratchPayLogo = "/assets/icons/scratchpay.png";

interface IScratchPaySection {
  title: string;
  description: string;
}

const SECTIONS: IScratchPaySection[] = [
  {
    title: "Verify your identity",
    description: "Confirm your phone number with a verification code.",
  },
  {
    title: "Enter your info",
    description:
      "Provide a few basic details so Scratch can confirm your eligibility.",
  },
  {
    title: "Review your monthly subscription plan",
    description:
      "See your monthly subscription amount and billing schedule. Subscription plans have a 12-month term.",
  },
  {
    title: "Review & accept your subscription agreement",
    description: "Review and accept the terms of your subscription program agreement.",
  },
  {
    title: "Add a payment method",
    description: "Enter your card details to activate your monthly subscription plan.",
  },
  {
    title: "You're all set!",
    description:
      "Head back to the GoodVets app to start taking advantage of your TotalCare plan benefits. ",
  },
];

const ScratchPayInfoModal = () => {
  const { closeModal } = useModal();

  useEffect(() => {
    userActivityLogger(MIXPANEL_EVENTS.SCRATCHPAY_INFO_MODAL_VIEWED, {});
  }, []);

  return (
    <Box
      className="flex flex-col w-full"
      sx={{ maxHeight: "90vh", maxWidth: "480px" }}
    >
      <Box className="flex justify-end pt-8 px-8">
        <Box
          className="cursor-pointer"
          role="button"
          aria-label="Close"
          onClick={closeModal}
        >
          <CloseIcon className="text-light-black" />
        </Box>
      </Box>

      <Box
        className="flex-1 overflow-y-auto px-8 py-6"
        sx={{
          "scrollbarWidth": "thin",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
          },
        }}
      >
        <Box className="flex flex-col gap-3 mb-6">
          <img
            src={scratchPayLogo}
            alt="ScratchPay"
            className="h-6 w-auto object-contain self-start"
          />
          <Typography
            component="h2"
            sx={{
              color: "black.main",
              fontFamily: "grenette-light-pro, serif",
              fontWeight: 600,
              fontSize: "30px",
              lineHeight: "32px",
            }}
          >
            Monthly billing with Scratch
          </Typography>
          <Typography
            sx={{
              color: "black.main",
              fontFamily: "geograph-regular, sans-serif",
              fontSize: "14px",
              lineHeight: "20px",
              fontWeight: 400,
            }}
          >
            Here&apos;s what to expect after selecting a monthly subscription option.
          </Typography>
        </Box>

        <Box className="flex flex-col gap-4">
          {SECTIONS.map((section, index) => (
            <Box key={index}>
              <Typography
                sx={{
                  color: "black.main",
                  fontFamily: "geograph-medium, sans-serif",
                  fontSize: "14px",
                  lineHeight: "20px",
                  fontWeight: 500,
                }}
              >
                {index + 1}. {section.title}
              </Typography>
              <Typography
                sx={{
                  color: "black.main",
                  fontFamily: "geograph-regular, sans-serif",
                  fontSize: "14px",
                  lineHeight: "20px",
                  fontWeight: 400,
                  mt: "4px",
                }}
              >
                {section.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="px-8 pb-8">
        <GvPrimaryButton
          type="button"
          onClick={closeModal}
          className="!h-10 !rounded-full !font-medium !text-sm"
        >
          Confirm
        </GvPrimaryButton>
      </Box>
    </Box>
  );
};

export default ScratchPayInfoModal;
